import { v4 as uuid } from 'uuid';
import type { DnDProps } from './types';
import type { CustomNode, CustomEdge, NodeToRenderProps } from '@src/components/custom-nodes/types';
import type { RenderItem } from '@src/scenario-editor/render-utils/types';
import { NODES_GAP } from '@src/consts';
import {
  getPlaceholderNodes,
  getNodesWithoutPlaceholders,
  recalculateNodesXPos,
  recalculateNodesYPosForNodes,
} from '@src/scenario-editor/utils';
import {
  unselectAllNodes,
  centerLayout,
  createDirectedEdge,
  recalculateHandlePositions,
  getIfConditionBlock,
  getBehaviorRulesBlock,
  getCommonNode,
  getEdgesForNewNodes,
} from '@src/scenario-editor/render-utils';
import { clearDndNodeItem, addOpenedInfoModal, setCurrentModalId, setHistoryNavigationFlag } from '@src/atoms';

type GetOnDropProps =
  Pick<
  DnDProps,
    'reactFlowInstance' | 'reactFlowWrapper'
  > & {
    setElementsSelectable: React.Dispatch<React.SetStateAction<boolean>>;
    setSelectedNodeProps: React.Dispatch<React.SetStateAction<NodeToRenderProps | null>>;
  };

export const getOnDrop = ({
  reactFlowInstance,
  reactFlowWrapper,
  setElementsSelectable,
  setSelectedNodeProps,
}: GetOnDropProps) => (event: React.DragEvent<HTMLDivElement>) => {
  event.preventDefault();

  const nodes = reactFlowInstance?.getNodes() as CustomNode[];
  const setNodes = reactFlowInstance?.setNodes;
  const setEdges = reactFlowInstance?.setEdges;
  const newNodeId = uuid();
  const dataTransferPayload = event.dataTransfer.getData('application/reactflow');

  if (!dataTransferPayload) {
    return;
  }

  const {
    title,
    titleDescription,
    rootLabelField,
    type,
    nodeType,
    icon,
    bgColor,
    cardType,
    handles,
    nodeInfoContent,
    validationSchema,
    isDraggable,
    ...rest
  } = JSON.parse(dataTransferPayload) as RenderItem;
  const nodesWithoutPlaceholders = getNodesWithoutPlaceholders(nodes);
  const placeholderNodes = getPlaceholderNodes(nodes);

  const restDataPropsToAdd = rest;
  delete restDataPropsToAdd.id;

  // check if the dropped element is valid
  if (typeof type === 'undefined' || !type) {
    return;
  }

  // check if the dropped element coordinates are in collision with some placeholder element
  if (!placeholderNodes.length && nodes.length > 0) {
    setElementsSelectable(true);
    return;
  }

  const placeholderNode = placeholderNodes.find((node: CustomNode) => {
    const nodeBounds = {
      top: node.position.y,
      bottom: node.position.y + (node.height ?? 0),
      left: node.position.x,
      right: node.position.x + (node.width ?? 0),
    };
    const mousePosition = reactFlowInstance?.screenToFlowPosition({
      x: event.clientX,
      y: event.clientY,
    });
    if (!mousePosition) {
      return;
    }

    return (
      mousePosition.x > nodeBounds.left &&
      mousePosition.x < nodeBounds.right &&
      mousePosition.y > nodeBounds.top &&
      mousePosition.y < nodeBounds.bottom
    );
  });

  if (!placeholderNode && nodes.length > 0) {
    setElementsSelectable(true);
    return setNodes?.(() => nodesWithoutPlaceholders);
  }

  clearDndNodeItem();
  setHistoryNavigationFlag(false);
  let position;

  // check if previous node exists and get its position
  if (placeholderNode) {
    position = {
      ...placeholderNode.position,
      y: placeholderNode.position.y + NODES_GAP,
    };
  } else {
    const viewportWidth = (reactFlowWrapper.current?.clientWidth) ?? 500;
    position = {
      x: viewportWidth / 2 + 200,
      y: NODES_GAP,
    };
  }

  const newCustomNode = getCommonNode({
    reactFlowInstance,
    handles: { t: handles.t ?? true, b: handles.b ?? true },
    type,
    title,
    titleDescription,
    position,
    nodeType,
    branchId: placeholderNode?.branchId ?? '',
    parentBranchId: placeholderNode?.parentBranchId ?? '',
    parentNodeId: placeholderNode?.parentNodeId ?? '',
    bgColor,
    cardType,
    svgIcon: icon,
    nodeInfoContent,
    validationSchema,
    rootLabelField,
    isDraggable,
    restDataPropsToAdd
  });

  let nodesToAppend: CustomNode[] = [newCustomNode];

  if (type === 'IfConditionNode') {
    const conditionBlock = getIfConditionBlock({
      reactFlowInstance,
      position,
      title,
      parentBranchId: placeholderNode?.parentBranchId ?? '',
      branchId: placeholderNode?.branchId ?? '',
      parentNodeId: placeholderNode?.parentNodeId ?? '',
      condStatements: [
        {op: 'or', value: []},
        {op: 'else', value: null}
      ]
    });

    nodesToAppend = [
      conditionBlock.ifConditionNode,
      ...conditionBlock.ifBranchNodes,
      conditionBlock.ifConditionNodeClose
    ];
  }

  if (type === 'BehaviorRulesNode') {
    const behaviorRulesBlock = getBehaviorRulesBlock({
      reactFlowInstance,
      position,
      title,
      parentBranchId: placeholderNode?.parentBranchId ?? '',
      branchId: placeholderNode?.branchId ?? '',
      parentNodeId: placeholderNode?.parentNodeId ?? '',
      conditions: [
        {
          rules: {
            operator: 'AND',
            criteria: []
          }
        },
        { type: 'else' }
      ]
    });

    nodesToAppend = [
      behaviorRulesBlock.ifConditionNode,
      ...behaviorRulesBlock.ifBranchNodes,
      behaviorRulesBlock.ifConditionNodeClose
    ];
  }

  unselectAllNodes(reactFlowInstance!);
  setSelectedNodeProps(nodesToAppend[0] as NodeToRenderProps);
  addOpenedInfoModal(nodesToAppend[0].id);
  setCurrentModalId(nodesToAppend[0].id);


  setNodes?.((nds) => {
    const ndsWithoutPlaceholders = getNodesWithoutPlaceholders(nds as CustomNode[]);
    const updatedNodes = recalculateNodesXPos(
      ndsWithoutPlaceholders.concat(nodesToAppend),
      reactFlowWrapper.current?.clientWidth,
      { instance: reactFlowInstance }
    );

    if (['IfConditionNode', 'BehaviorRulesNode'].includes(type)) {
      const recalculatedNode = updatedNodes.find((node) => node.id === nodesToAppend[0].id) ?? nodes[0];
      centerLayout(reactFlowInstance!)({
        coords: {
          x: recalculatedNode.position.x + 100,
          y: recalculatedNode.position.y + 50
        }
      })
    }

    return updatedNodes;
  });

  setEdges?.((eds) => {
    if (nodes.length === 0) {
      return eds;
    }
    // delete the edge that has placeholderNode.parentNodeId as source
    const edgeToDelete = eds.find((edge) => edge.source === placeholderNode?.parentNodeId);

    if (!edgeToDelete) {
      return eds.concat(createDirectedEdge(placeholderNode?.parentNodeId ?? '', newNodeId));
    }

    const newTarget = edgeToDelete?.target;
    let newEdges = eds;
    if (edgeToDelete) {
      newEdges = eds.filter((edge) => edge.id !== edgeToDelete.id);
    }

    return newEdges.concat(getEdgesForNewNodes({
      sourceId: placeholderNode?.parentNodeId ?? '',
      targetId: newTarget,
      nodesToAppend: nodesToAppend.map((node) => node.id),
      isSealed: nodeType === 'message:goto'
    }));
  });

  setTimeout(() => {
    setElementsSelectable(true);
    setNodes?.(() => {
      const nodes = reactFlowInstance?.getNodes() as CustomNode[];
      const edges = reactFlowInstance?.getEdges() as CustomEdge[];
      recalculateNodesYPosForNodes(nodes, edges);
      recalculateNodesXPos(nodes, undefined, { instance: reactFlowInstance });
      return nodes;
    });
    setEdges?.(() => {
      const nodes = reactFlowInstance?.getNodes() as CustomNode[];
      const edges = reactFlowInstance?.getEdges() as CustomEdge[];
      recalculateHandlePositions(nodes, edges);
      return edges;
    });
  }, 100);
};
